import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight, Download, PhoneCall } from 'lucide-react';
import { callsAPI } from '../../../api/client';
import { useTenant } from '../../../context/TenantContext';
import { apiError } from '../../../lib/host';
import CallDialog, { prettyNumber } from '../CallDialog';
import { ErrorBanner, PageHeading, saveDownload, useBasic } from '../ui';
import { EMPTY_FILTERS, LIVE_STATUSES, rangeParams } from './callModel';
import { CallingBanner } from './components/CallBadges';
import CallDetailsPanel from './components/CallDetailsPanel';
import CallFilters from './components/CallFilters';
import CallStats from './components/CallStats';
import CallsTable from './components/CallsTable';
import Pager from './components/Pager';
import './Calls.css';

/**
 * Calls & Conversations, laid out like the Super Admin's Subscriber Control: figures, a filter bar, the call history
 * (paged on the server) and the selected call's details on the right.
 */
export default function CallsPage() {
  const navigate = useNavigate();
  const { notify, plan, upgrade } = useBasic();
  const { can } = useTenant();
  const canCall = can('calls.make');
  const [params, setParams] = useSearchParams();
  const [calls, setCalls] = useState(null);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [telephony, setTelephony] = useState(null);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  // The dashboard links here with a filter already chosen, e.g. /calls?status=failed.
  const [filters, setFilters] = useState(() => ({ ...EMPTY_FILTERS, direction: params.get('direction') || '', status: params.get('status') || '', range: params.get('range') || '' }));
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [activeId, setActiveId] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setQuery(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);
  useEffect(() => { setPage(1); setActiveId(null); }, [query, filters, pageSize]);

  const filterParams = useCallback(() => ({
    ...(query && { q: query }), ...(filters.direction && { direction: filters.direction }), ...(filters.status && { status: filters.status }),
    ...rangeParams(filters.range, filters.from, filters.to),
  }), [query, filters]);

  // Filters can change faster than the server answers; only the latest request may update the list.
  const latestRequest = useRef(0);
  const load = useCallback(() => {
    const n = ++latestRequest.current;
    callsAPI.list({ ...filterParams(), limit: pageSize, offset: (page - 1) * pageSize }).then((res) => {
      if (n !== latestRequest.current) return;
      setCalls(res.data.items); setTotal(res.data.total); setError('');
    }).catch((err) => n === latestRequest.current && setError(apiError(err, 'Could not load calls')));
    callsAPI.stats().then((res) => setStats(res.data)).catch(() => {});
  }, [filterParams, page, pageSize]);

  useEffect(load, [load]);
  useEffect(() => { callsAPI.telephony().then((res) => setTelephony(res.data)).catch(() => {}); }, []);

  // While a call is connecting, refresh so its status updates without reloading the page.
  const live = calls?.some((c) => LIVE_STATUSES.includes(c.status));
  useEffect(() => {
    if (!live) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [live, load]);

  useEffect(() => {
    if (params.get('call') && canCall) {
      setDialog({});
      setParams({}, { replace: true });
    }
  }, [params, setParams, canCall]);

  // The selected call, or the newest one on this page until a row is chosen.
  const shownId = activeId ?? calls?.[0]?.id ?? null;

  const placed = (call) => {
    setDialog(null);
    notify(`Calling your phone now. Answer it to be connected to ${call.contact_name || prettyNumber(call.other_number)}.`);
    load();
  };
  const callBack = (c) => setDialog({ number: c.other_number, contact: c.contact_id ? { id: c.contact_id, name: c.contact_name, phone: c.other_number } : null });
  const onAction = (key, c) => {
    if (key === 'view') setActiveId(c.id);
    else if (key === 'open') navigate(`/calls/${c.id}`);
    else if (key === 'call') callBack(c);
    else if (key === 'copy') navigator.clipboard?.writeText(c.other_number).then(() => notify(`${prettyNumber(c.other_number)} copied.`)).catch(() => {});
  };

  const exportCsv = async () => {
    if (!total) {
      notify('No calls to export yet.');
      return;
    }
    try {
      saveDownload(await callsAPI.exportCsv(filterParams()), 'calls');
      notify(`Exported ${total} call${total === 1 ? '' : 's'}.`);
    } catch (err) {
      setError(apiError(err, 'Could not export calls'));
    }
  };
  const filtered = query || filters.direction || filters.status || filters.range;
  const reset = () => { setSearch(''); setQuery(''); setFilters(EMPTY_FILTERS); };

  return (
    <section className="page-view cl-page">
      <PageHeading title="Calls & Conversations" subtitle="Every call to and from your company numbers, with outcomes and recordings.">
        <button className="secondary-action" onClick={exportCsv}><Download size={14} />Export</button>
        {canCall && <button className="primary-action" onClick={() => setDialog({})}><PhoneCall size={14} />Make a call</button>}
      </PageHeading>
      <CallingBanner status={telephony} />
      <ErrorBanner message={error} />

      <CallStats stats={stats} onFilter={(f) => setFilters({ ...EMPTY_FILTERS, ...f })} />
      <CallFilters search={search} onSearch={setSearch} filters={filters} onChange={setFilters} onReset={reset} />

      <div className="cl-layout">
        <section className="panel cl-list">
          <div className="cl-list-head"><div><h2>Call history</h2><small>{calls === null ? 'Loading…' : `${total} call${total === 1 ? '' : 's'}${filtered ? ' match your filters' : ''}`}</small></div></div>
          {calls === null && <div className="empty-state cl-empty">Loading calls…</div>}
          {calls && calls.length > 0 && (
            <CallsTable calls={calls} offset={(page - 1) * pageSize} activeId={shownId} canCall={canCall} onView={(c) => setActiveId(c.id)} onAction={onAction} />
          )}
          {calls?.length === 0 && <div className="empty-state cl-empty">{filtered ? 'No calls match your filters.' : 'No calls yet. Calls to and from your company numbers appear here.'}</div>}
          {calls && total > 0 && <Pager page={page} pageSize={pageSize} total={total} onPage={setPage} onPageSize={setPageSize} />}
        </section>

        <div className="cl-side">
          {shownId && <CallDetailsPanel key={shownId} callId={shownId} canCall={canCall} onCall={callBack} onOpen={(c) => navigate(`/calls/${c.id}`)} onSelect={setActiveId} />}
          <section className="panel cl-card">
            <h2>Quick Actions</h2>
            <div className="page-action-list">
              {canCall && <button onClick={() => setDialog({})}><span className="action-dot dot-0">✆</span><span><b>Make a call</b><small>We ring you, then connect the customer</small></span><ArrowRight size={14} /></button>}
              <button onClick={exportCsv}><span className="action-dot dot-1">↓</span><span><b>Export call report</b><small>Download a CSV of the current list</small></span><ArrowRight size={14} /></button>
              <button onClick={() => navigate('/phone-numbers')}><span className="action-dot dot-2">⚙</span><span><b>Call routing</b><small>Choose who answers incoming calls</small></span><ArrowRight size={14} /></button>
            </div>
          </section>
          <section className="panel cl-card">
            <div className="cl-card-head"><h2>Plan Access</h2><span className="active-pill"><i />{plan}</span></div>
            <p className="access-copy">Call records are stored in your company's own database. Recordings are kept by the phone provider.</p>
            <button className="upgrade-full" onClick={upgrade}>Review subscription <ArrowRight size={14} /></button>
          </section>
        </div>
      </div>

      {dialog && <CallDialog contact={dialog.contact} number={dialog.number} onClose={() => setDialog(null)} onPlaced={placed} />}
    </section>
  );
}
